import type { MeResponse } from '@crew/shared';

/**
 * מכונת המצבים של ההתחברות — טהורה ונבדקת בבדיקות יחידה.
 *
 * Firebase אומר "מי המשתמש"; רק ה-Worker אומר "האם הוא מנהל".
 * לכן יש מצב ביניים (verifying) שבו המשתמש מחובר ל-Firebase אבל עדיין
 * לא אושר, ושום מסך ניהול לא מוצג בו.
 */

export type AuthStatus =
  | { kind: 'initializing' }
  | { kind: 'signed_out'; notice: string | null }
  | { kind: 'verifying'; uid: string }
  | { kind: 'authorized'; me: MeResponse }
  | { kind: 'error'; uid: string; message: string };

export type AuthEvent =
  | { type: 'firebase_user'; uid: string | null }
  | { type: 'me_ok'; me: MeResponse }
  | { type: 'me_denied' }
  | { type: 'me_failed'; message: string }
  | { type: 'retry' };

export const DENIED_NOTICE = 'לחשבון זה אין הרשאת ניהול. ההתחברות בוטלה.';

export const initialAuthStatus: AuthStatus = { kind: 'initializing' };

export function authReducer(state: AuthStatus, event: AuthEvent): AuthStatus {
  switch (event.type) {
    case 'firebase_user':
      if (event.uid === null) {
        // שומרים הודעת "אין הרשאה" אחרי היציאה האוטומטית, כדי שהמשתמש יבין מה קרה.
        const notice = state.kind === 'signed_out' ? state.notice : null;
        return { kind: 'signed_out', notice };
      }
      // רענון טוקן לאותו משתמש לא משנה את המצב.
      if (state.kind === 'authorized' && state.me.uid === event.uid) return state;
      if ((state.kind === 'verifying' || state.kind === 'error') && state.uid === event.uid) return state;
      return { kind: 'verifying', uid: event.uid };

    case 'me_ok':
      if (state.kind !== 'verifying' || state.uid !== event.me.uid) return state;
      return { kind: 'authorized', me: event.me };

    case 'me_denied':
      if (state.kind !== 'verifying') return state;
      return { kind: 'signed_out', notice: DENIED_NOTICE };

    case 'me_failed':
      if (state.kind !== 'verifying') return state;
      return { kind: 'error', uid: state.uid, message: event.message };

    case 'retry':
      if (state.kind !== 'error') return state;
      return { kind: 'verifying', uid: state.uid };
  }
}

/** האם מותר להציג מסכי ניהול. */
export function canAccessAdmin(state: AuthStatus): state is Extract<AuthStatus, { kind: 'authorized' }> {
  return state.kind === 'authorized';
}
